import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Loader2, RefreshCw, X } from 'lucide-react';
import { Checkbox, SearchSelect } from './FieldComponents';
import { publicFormsApi } from '../services/publicFormsApi';
import { caseDbApiEndpoint } from '../utils/apiOrigin';
import { canSendBulkCommunications } from '../utils/bulkCommunicationAccess';
import '../styles/PublicFormSmsBatch.css';

const MAX_CASES = 50;
const errorText = (error) => error?.message || 'CaseDB could not complete this request.';
const positiveId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const batchIdIsValid = (value) => typeof value === 'string' && /^[a-f0-9]{32}$/.test(value);
const statusText = (value) => ({
  queued: 'Queued', pending: 'Queued', processing: 'Processing', sent: 'Sent',
  confirmation_pending: 'Awaiting provider confirmation', unknown: 'Awaiting provider confirmation',
  failed: 'Failed', blocked: 'Not queued', eligible: 'Ready', complete: 'Complete',
  cancelled: 'Cancelled', skipped: 'Skipped',
  delivery_failed: 'Delivery failed', awaiting_provider_confirmation: 'Awaiting provider confirmation',
}[value] || 'Status unavailable');
const reasonText = (value) => ({
  case_unavailable: 'Case is unavailable or you no longer have access.',
  no_sms_recipient_or_opted_out: 'No eligible SMS recipient, or the recipient has opted out.',
  form_not_eligible_for_case: 'The form is not eligible for this case. Check its case type and access settings.',
  duplicate_phone_conflict: 'Another selected case uses this phone. Choose which case should receive a link.',
  delivery_failed: 'Delivery failed. Check the batch before sending again.',
  awaiting_provider_confirmation: 'Delivery has not been confirmed. Do not resend yet.',
}[value] || (value ? 'This recipient needs review.' : ''));

export const validateSmsBatchPreview = (preview, request) => {
  if (!preview || !Array.isArray(preview.rows) || !Array.isArray(preview.case_ids)
    || preview.form_id !== request.form_id || preview.message !== request.message
    || typeof preview.review_hash !== 'string' || !/^[a-f0-9]{64}$/.test(preview.review_hash)
    || !preview.review_expires_at || preview.rows.length !== request.case_ids.length) return false;
  const wanted = [...request.case_ids].sort((a, b) => a - b).join(',');
  const ids = preview.rows.map((row) => Number(row.case_id));
  const eligible = preview.rows.filter((row) => row.status === 'eligible').length;
  return new Set(ids).size === ids.length && ids.every(positiveId)
    && [...ids].sort((a, b) => a - b).join(',') === wanted
    && [...preview.case_ids].map(Number).sort((a, b) => a - b).join(',') === wanted
    && preview.rows.every((row) => ['eligible', 'blocked'].includes(row.status))
    && Number(preview.counts?.selected) === ids.length
    && Number(preview.counts?.eligible) === eligible
    && Number(preview.counts?.blocked) === ids.length - eligible;
};

const newRequestId = () => {
  if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
  if (typeof window.crypto?.getRandomValues !== 'function') throw new Error('This browser cannot create a secure request ID.');
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const renderCaseOption = (option) => <span className="mini-case-meta">
  <span className="mini-case-name"><span>{option.label}</span><span className="subtext">({option.value})</span></span>
  {option.phase && <span className="mini-case-info subtext">{option.phase}</span>}
</span>;

const PublicFormSmsBatch = ({ form, endpoint, casesEndpoint, onBack, sendEnabled = false, accessLevel = '' }) => {
  const bulkSender = canSendBulkCommunications(accessLevel);
  const canSend = sendEnabled && bulkSender;
  const [selected, setSelected] = useState(new Map());
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [caseError, setCaseError] = useState('');
  const [review, setReview] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [batch, setBatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const operationRef = useRef(0);
  const caseSearchRef = useRef(null);
  const locked = !canSend || busy || Boolean(pendingConfirmation) || Boolean(batch);

  useEffect(() => () => { operationRef.current += 1; }, []);
  useEffect(() => {
    if (!pendingConfirmation) return undefined;
    const warnBeforeLeaving = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [pendingConfirmation]);
  useEffect(() => () => { caseSearchRef.current?.abort(); }, [locked, form.case_type_id, casesEndpoint]);
  const loadCaseOptions = useCallback(async (query = '') => {
    if (locked || !positiveId(form.case_type_id)) return [];
    caseSearchRef.current?.abort();
    const controller = new AbortController();
    caseSearchRef.current = controller;
    setCaseError('');
    try {
      // Use the case-list contract: navigation search does not apply the
      // form's case-type filter. SearchSelect handles the search debounce.
      const url = new URL(casesEndpoint || caseDbApiEndpoint('cases.php'), window.location.href);
      // Fetch more than the selection cap so already-selected rows cannot
      // exhaust a broad query; the shared picker displays 15 options at a time.
      Object.entries({ page: 1, per_page: 100, case_types: form.case_type_id,
        search: query.trim(), sort_by: 'case_name', sort_order: 'ASC' }).forEach(([key, value]) => {
        if (value !== '') url.searchParams.set(key, String(value));
      });
      const response = await fetch(url.toString(), { credentials: 'include', cache: 'no-store', signal: controller.signal });
      const payload = await response.json();
      if (!response.ok || payload.success !== true || !Array.isArray(payload.cases)) throw new Error(payload.message || 'Cases could not be loaded.');
      if (controller.signal.aborted) return [];
      // Pinned rows are returned separately but have the same server filters.
      const matchingRows = [...(Array.isArray(payload.pinned_cases) ? payload.pinned_cases : []), ...payload.cases];
      const uniqueRows = new Map(matchingRows.filter((item) => positiveId(item.case_id)).map((item) => [Number(item.case_id), item]));
      return [...uniqueRows.values()].filter((item) => !selected.has(Number(item.case_id))).map((item) => ({
        value: String(item.case_id), label: item.case_name || `Case ${item.case_id}`, phase: item.phase_name,
      }));
    } catch (requestError) {
      if (!controller.signal.aborted) setCaseError(errorText(requestError));
      return [];
    }
  }, [casesEndpoint, form.case_type_id, locked, selected]);

  useEffect(() => {
    let current = true;
    publicFormsApi.smsBatches(form.id, { endpoint }).then((payload) => {
      if (current) setHistory(Array.isArray(payload.batches) ? payload.batches : []);
    }).catch((requestError) => { if (current) setHistoryError(errorText(requestError)); });
    return () => { current = false; };
  }, [endpoint, form.id]);

  const resetReview = () => { operationRef.current += 1; setReview(null); setAcknowledged(false); setError(''); };
  const selectCases = (items, checked) => {
    if (locked) return;
    const next = new Map(selected);
    items.forEach((item) => checked ? next.set(Number(item.id), item.case_name || `Case ${item.id}`) : next.delete(Number(item.id)));
    if (next.size > MAX_CASES) { setError(`Choose up to ${MAX_CASES} cases for one batch. No additional cases were selected.`); return; }
    resetReview(); setSelected(next);
  };
  const preview = async () => {
    if (locked || !selected.size || !message.trim()) return;
    const generation = ++operationRef.current;
    const request = { form_id: Number(form.id), case_ids: [...selected.keys()].sort((a, b) => a - b), message: message.trim() };
    setBusy(true); setError(''); setReview(null); setAcknowledged(false);
    try {
      const payload = await publicFormsApi.previewSmsBatch(request, { endpoint });
      if (generation !== operationRef.current) return;
      if (!validateSmsBatchPreview(payload.preview, request)) throw new Error('The recipient review was incomplete. Nothing was queued; review the batch again.');
      setReview({ request, preview: payload.preview });
    } catch (requestError) { if (generation === operationRef.current) setError(errorText(requestError)); }
    finally { if (generation === operationRef.current) setBusy(false); }
  };
  const recordBatch = (next) => {
    if (!next || !batchIdIsValid(next.id) || !Array.isArray(next.rows)) throw new Error('The batch result is incomplete. Check its status before starting another batch.');
    setBatch(next); setPendingConfirmation(null); setError('');
    setHistory((items) => [next, ...items.filter((item) => item.id !== next.id)].slice(0, 20));
  };
  const confirm = async () => {
    if (!canSend || busy || batch || (!pendingConfirmation && (!review || !acknowledged || !review.preview.counts.eligible))) return;
    let request = pendingConfirmation;
    try {
      request = request || { ...review.request, confirmed: true, request_id: newRequestId(),
        review_hash: review.preview.review_hash, review_expires_at: review.preview.review_expires_at };
    } catch (requestError) { setError(errorText(requestError)); return; }
    setPendingConfirmation(request); setBusy(true); setError('');
    const generation = ++operationRef.current;
    try {
      const payload = await publicFormsApi.confirmSmsBatch(request, { endpoint });
      if (generation === operationRef.current) recordBatch(payload.batch);
    } catch (requestError) {
      if (generation !== operationRef.current) return;
      // Only explicit pre-enqueue rejection permits a new review/request ID.
      if (['review_expired', 'review_changed', 'batch_rejected'].includes(requestError.code)) {
        setPendingConfirmation(null); setReview(null); setAcknowledged(false);
      }
      setError(errorText(requestError));
    } finally { if (generation === operationRef.current) setBusy(false); }
  };
  const refresh = async (batchId = batch?.id) => {
    if (busy) return;
    setBusy(true); setError('');
    const generation = ++operationRef.current;
    try {
      const request = batchId ? { batch_id: batchId } : { request_id: pendingConfirmation.request_id };
      const payload = await publicFormsApi.smsBatchStatus(request, { endpoint });
      if (generation === operationRef.current) recordBatch(payload.batch);
    } catch (requestError) { if (generation === operationRef.current) setError(errorText(requestError)); }
    finally { if (generation === operationRef.current) setBusy(false); }
  };
  const clearBatch = () => { setBatch(null); setSelected(new Map()); setMessage(''); resetReview(); };

  return <div className="public-form-sms-batch">
    <header id="page-header" className="public-forms-header">
      <div className="public-forms-header-copy"><h2>Text form links</h2><p className="subtext">{form.name || form.public_title} · Each text receives its own case-specific link.</p></div>
      <button type="button" className="action quaternary" onClick={onBack} disabled={busy || Boolean(pendingConfirmation)}><ArrowLeft size={16} /> Back to forms</button>
    </header>
    {!canSend && <p role="status">{bulkSender ? 'New bulk texts are unavailable for this form.' : 'Only Global Admin can send bulk texts.'} You can still check previous batches below.</p>}
    {error && <p className="public-form-feature-error" role="alert">{error}</p>}
    {pendingConfirmation && <section className="public-form-batch-panel" aria-label="Confirmation recovery">
      <h3>Check this batch before trying another</h3>
      <p>The confirmation may still be processing. Check its status or retry this same confirmation. Do not start a replacement batch while the outcome is unknown.</p>
      <div className="public-form-batch-actions"><button type="button" className="action quaternary" disabled={busy} onClick={() => refresh()}>Check batch status</button><button type="button" className="action quaternary" disabled={busy || !canSend} onClick={confirm}>Retry same confirmation</button></div>
    </section>}
    {!batch && canSend && <>
      <section className="public-form-batch-panel" aria-labelledby="batch-cases-title">
        <h3 id="batch-cases-title">Choose cases</h3>
        <p className="subtext">Search by case name, client, or case ID. Add up to {MAX_CASES} cases matching this form’s case type. Archived cases are excluded.</p>
        <div className="form-group full nm public-form-batch-case-search">
          <label className="subtext" htmlFor="public-form-batch-case-search">Cases</label>
          <SearchSelect
            value=""
            inputId="public-form-batch-case-search"
            ariaLabel="Search cases"
            placeholder="Search and add cases…"
            loadOptions={loadCaseOptions}
            loadOptionsMinChars={2}
            returnOptionObject
            renderOption={renderCaseOption}
            disabled={locked || !positiveId(form.case_type_id)}
            onChange={(option) => {
              if (positiveId(option?.value) && !selected.has(Number(option.value))) {
                selectCases([{ id: option.value, case_name: option.label }], true);
              }
            }}
          />
        </div>
        {!positiveId(form.case_type_id) && <p role="alert">This form needs a case type before cases can be selected.</p>}
        {caseError && <p role="alert">{caseError}</p>}
        <div className="public-form-batch-actions"><span className="subtext" role="status">{selected.size} selected</span>{selected.size > 0 && <button type="button" className="action-tiny quaternary" disabled={locked} onClick={() => { resetReview(); setSelected(new Map()); }}>Clear selection</button>}</div>
        {selected.size > 0 && <ul className="selected-cases public-form-batch-selected-cases" aria-label="Selected cases">{[...selected].map(([id, name]) => <li className="selected-case" key={id}>
          <span>{name} <small className="subtext">({id})</small></span>
          <button type="button" className="action-micro quaternary" aria-label={`Remove ${name}`} disabled={locked} onClick={() => selectCases([{ id }], false)}><X size={14} aria-hidden="true" /></button>
        </li>)}</ul>}
      </section>
      <section className="public-form-batch-panel" aria-labelledby="batch-message-title">
        <h3 id="batch-message-title">Write the message</h3>
        <div className="form-group nm"><label htmlFor="batch-message">Message to each claimant</label><textarea id="batch-message" rows={4} maxLength={800} value={message} disabled={locked} onChange={(event) => { resetReview(); setMessage(event.target.value); }} /></div>
        <p className="subtext">CaseDB adds the individual form link after your message. Do not paste a recipient link here. Each link expires in 7 days and accepts one submission. Longer messages may use multiple SMS segments.</p>
        <button type="button" className="action" disabled={locked || !selected.size || !message.trim()} onClick={preview}>{busy && <Loader2 size={16} className="spinner" />} Review recipients and messages</button>
      </section>
      {review && <section className="public-form-batch-panel" aria-labelledby="batch-review-title">
        <h3 id="batch-review-title">Review before queueing</h3>
        <p>{review.preview.counts.eligible} ready · {review.preview.counts.blocked} blocked. {pendingConfirmation ? 'Confirmation submitted; check batch status before trying again.' : 'Nothing has been sent or queued yet.'}</p>
        <div className="public-forms-table-wrap"><table className="public-forms-table"><thead><tr><th>Case</th><th>Recipient</th><th>Status</th><th>Message</th></tr></thead><tbody>{review.preview.rows.map((row) => <tr key={row.case_id}><td>{row.case_name || `Case ${row.case_id}`}</td><td>{row.recipient_name || 'Unavailable'}<br />{row.phone_mask}</td><td>{statusText(row.status)}<small>{reasonText(row.reason_code)}</small></td><td className="public-form-batch-message">{row.message_preview || 'Not queued'}</td></tr>)}</tbody></table></div>
        <div className="form-group nm public-form-batch-acknowledgement"><Checkbox value={acknowledged} disabled={locked} top
          onChange={() => setAcknowledged((value) => !value)}
          ariaLabel="I have reviewed the recipients and messages. Queue only the ready recipients."
          label="I have reviewed the recipients and messages. Queue only the ready recipients."
        /></div>
        <button type="button" className="action" disabled={locked || !acknowledged || !review.preview.counts.eligible} onClick={confirm}>Queue {review.preview.counts.eligible} text messages</button>
      </section>}
    </>}
    {batch && <section className="public-form-batch-panel" aria-labelledby="batch-status-title">
      <h3 id="batch-status-title">Batch {batch.id}: {statusText(batch.status)}</h3>
      <p>Queued is not sent. Messages follow the existing SMS queue’s rate limits. Check status before contacting anyone again; uncertain deliveries are not automatically retried.</p>
      <div className="public-form-batch-actions"><button type="button" className="action quaternary" disabled={busy} onClick={() => refresh()}><RefreshCw size={16} /> Refresh status</button>{canSend && <button type="button" className="action quaternary" disabled={busy} onClick={clearBatch}>Start a different batch</button>}</div>
      <div className="public-forms-table-wrap"><table className="public-forms-table"><thead><tr><th>Case</th><th>Status</th><th>Details</th></tr></thead><tbody>{batch.rows.map((row) => <tr key={row.case_id}><td>{row.case_name || `Case ${row.case_id}`}</td><td>{statusText(row.status)}</td><td>{reasonText(row.reason_code)}</td></tr>)}</tbody></table></div>
    </section>}
    <section className="public-form-batch-panel" aria-labelledby="batch-history-title"><h3 id="batch-history-title">Recent batches for this form</h3><p className="subtext">Check an existing batch before sending a replacement.</p>{historyError && <p role="alert">{historyError}</p>}{history.length ? <div className="public-form-batch-actions">{history.map((item) => <button type="button" className="action-tiny quaternary" key={item.id} disabled={busy || Boolean(pendingConfirmation)} onClick={() => refresh(item.id)}>Batch {item.id} · {statusText(item.status)}</button>)}</div> : <p>No recent batches loaded.</p>}</section>
  </div>;
};

export default PublicFormSmsBatch;
